import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Briefcase, CheckCircle2, Building2, Globe, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { recruitmentServices } from '../data/mockData';

const Recruitment = () => {
  return (
    <div className="min-h-screen pt-28 md:pt-36">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <span className="bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium border border-red-600/30">
                Recruitment & Talent Advisory
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Strategic Talent Acquisition for <span className="text-red-500">UAE Businesses</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Comprehensive recruitment solutions for white-collar and blue-collar positions across all industries.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 h-12">
                Discuss Hiring Needs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* White-Collar & Blue-Collar Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recruitment Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From executive search to mass mobilization, we deliver the right talent at the right time.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* White-Collar */}
            <Card className="border-t-4 border-t-red-600">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {recruitmentServices.whiteCollar.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {recruitmentServices.whiteCollar.description}
                </p>

                <h4 className="text-sm font-semibold text-gray-900 mb-4">Typical Roles:</h4>
                <div className="grid grid-cols-1 gap-3">
                  {recruitmentServices.whiteCollar.roles.map((role, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-gray-700">{role}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Blue-Collar */}
            <Card className="border-t-4 border-t-red-600">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {recruitmentServices.blueCollar.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {recruitmentServices.blueCollar.description}
                </p>

                <h4 className="text-sm font-semibold text-gray-900 mb-4">Typical Roles:</h4>
                <div className="grid grid-cols-1 gap-3">
                  {recruitmentServices.blueCollar.roles.map((role, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-gray-700">{role}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industry Coverage */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Industry Coverage
              </h2>
              <p className="text-lg text-gray-600">
                Specialized recruitment expertise across UAE's key sectors.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                'Oil & Gas',
                'Construction',
                'Manufacturing',
                'Hospitality',
                'Healthcare',
                'Technology',
                'Retail',
                'Logistics',
                'Real Estate'
              ].map((industry, idx) => (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg text-center font-semibold text-gray-900 border border-gray-200 hover:border-red-600 hover:shadow-md transition-all"
                >
                  {industry}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Mobilization */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Compliance & Mobilization
              </h2>
              <p className="text-lg text-gray-600">
                End-to-end recruitment management ensuring regulatory compliance and rapid deployment.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ClipboardCheck className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">UAE Labor Law Compliance</h3>
                  <p className="text-gray-600">
                    Full adherence to UAE employment regulations, visa requirements, and contractual standards.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">International Sourcing</h3>
                  <p className="text-gray-600">
                    Global talent network with expertise in visa processing and international onboarding.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Rapid Mobilization</h3>
                  <p className="text-gray-600">
                    Efficient deployment processes ensuring workforce availability when you need it.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose FinVestra for Recruitment?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Deep Market Access</h3>
                  <p className="text-gray-600">
                    Extensive talent networks across UAE and international markets.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Quality Screening</h3>
                  <p className="text-gray-600">
                    Rigorous assessment processes ensuring only qualified candidates.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Fast Turnaround</h3>
                  <p className="text-gray-600">
                    Efficient processes delivering candidates within agreed timelines.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">End-to-End Support</h3>
                  <p className="text-gray-600">
                    From sourcing to onboarding—complete recruitment management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Looking to Build Your Team?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Connect with us to discuss your hiring requirements and how we can support your workforce needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 h-12">
              Start Hiring Process
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Recruitment;
